import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import type { DisplayComponentProps } from '../types';
import type { CustomComponentId } from './componentManifest';

export interface ConfigOption {
	name: string;
	type: 'boolean' | 'string' | 'number' | 'select';
	label: string;
	description?: string;
	defaultValue?: unknown;
	options?: Array<{ value: string; label: string }>;
}

export interface CustomComponent {
	id: CustomComponentId;
	displayName: string;
	description?: string;
	icon?: IconDefinition;
	component: React.ComponentType<DisplayComponentProps>;
	configOptions?: Array<ConfigOption>;
	category?: string;
}

class ComponentRegistryClass {
	private readonly components = new Map<CustomComponentId, CustomComponent>();
	private readonly listeners: Set<() => void> = new Set();

	/**
	 * Register a custom component for use in list builder.
	 *
	 * Idempotent: re-registering the SAME component reference under
	 * the same id is a no-op. Modules call `register()` at the top of
	 * their `module.config.ts`, so Vite HMR (and any test harness that
	 * re-evaluates the module) would otherwise crash the whole app on
	 * the second pass — which was the failure mode being seen in the
	 * console as `Duplicate component registration: ...`.
	 *
	 * If the id is already registered with a DIFFERENT component
	 * reference, that's a genuine cross-module collision; we keep the
	 * existing entry and warn loudly in the console rather than throw,
	 * so a misnamed component in one module can't take down every
	 * other surface that uses the registry.
	 */
	register(component: CustomComponent): void {
		if (component.id == null) {
			throw new Error('Component id is required');
		}

		if (component.component == null) {
			throw new Error('Component implementation is required');
		}

		const existing = this.components.get(component.id);
		if (existing != null) {
			if (existing.component === component.component) {
				// HMR / re-import — same code, same reference, nothing to do.
				return;
			}
			// eslint-disable-next-line no-console
			console.warn(
				`[ComponentRegistry] Duplicate component id "${component.id}" — keeping the first registration. ` +
					`Pick a unique id for the new component to avoid this collision.`,
			);
			return;
		}

		this.components.set(component.id, component);
		this.notifyListeners();
	}

	/**
	 * Unregister a custom component by ID
	 */
	unregister(id: CustomComponentId): boolean {
		const result = this.components.delete(id);

		if (result) {
			this.notifyListeners();
		}

		return result;
	}

	/**
	 * Get a specific component by ID
	 */
	getComponent(id: string): CustomComponent | undefined {
		return this.components.get(id as CustomComponentId);
	}

	/**
	 * Get all registered components
	 */
	getAllComponents(): Array<CustomComponent> {
		return Array.from(this.components.values());
	}

	/**
	 * Get components grouped by category
	 */
	getComponentsByCategory(): Record<string, Array<CustomComponent>> {
		const grouped: Record<string, Array<CustomComponent>> = {};
		const allComponents = this.getAllComponents();

		allComponents.forEach((component) => {
			const category = component.category ?? 'Other';

			if (grouped[category] == null) {
				grouped[category] = [];
			}

			grouped[category].push(component);
		});

		return grouped;
	}

	/**
	 * Check if a component is registered
	 */
	hasComponent(id: string): boolean {
		return this.components.has(id as CustomComponentId);
	}

	/**
	 * Subscribe to registry changes
	 */
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);

		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		this.listeners.forEach((listener) => {
			listener();
		});
	}

	/**
	 * Clear all registered components
	 */
	clear(): void {
		this.components.clear();
		this.notifyListeners();
	}
}

// Export singleton instance
export const ComponentRegistry = new ComponentRegistryClass();

// Hook to use component registry in React components
export function useComponentRegistry() {
	const [, forceUpdate] = React.useState({});

	React.useEffect(() => {
		const unsubscribe = ComponentRegistry.subscribe(() => {
			forceUpdate({});
		});

		return unsubscribe;
	}, []);

	return {
		components: ComponentRegistry.getAllComponents(),
		componentsByCategory: ComponentRegistry.getComponentsByCategory(),
		getComponent: (id: string) => ComponentRegistry.getComponent(id),
		hasComponent: ComponentRegistry.hasComponent.bind(ComponentRegistry),
	};
}
